import { Fragment, useState, type ReactElement } from "react";
import { BODY_TYPE, pixelType } from "odyssey-shared";
import { Button, Checkbox, Dropdown, KIT_METRICS, Segmented, TextLink, centreOffset, logical } from "../../ui/kit/index.ts";
import { CONTROL_ROWS, MOVE_SETS, actionName, type ActionId } from "../controls/actions.ts";
import { DEFAULT_BINDINGS, chooseKey, clashesOf, refusalOf, type Clash } from "../controls/bindings.ts";
import type { RefusedReason } from "../controls/capture.ts";
import { LAYOUT_CHOICES, codeLabel, pageLayout, type KeyLayout, type LayoutChoice } from "../controls/layout.ts";
import type { RunMode } from "../run/intent.ts";
import { KEY_CAP, KeyCap, KeyNote, type CapState, type NoteLine } from "./key-cap.tsx";
import { RUN_MODE_CHOICES } from "./run-choices.ts";
import { isChanged, keySetting, type SettingId, type SettingsDraft } from "./settings-model.ts";
import { SETTINGS_WINDOW } from "./settings-sections.ts";
import { SettingsRow, changedMarkId, noteId, rowHeight } from "./settings-panes.tsx";
import { capName, refusedKeyNote } from "./wording.ts";
import { say } from "../../words.ts";

/**
 * Settings' Controls pane (P1.9-T50; ADR-061's amendment "key rebinding", decisions 2, 6, 7 and 8; the design's
 * `SettingsPane` in its `controls`, `rebind` and `conflict` states, a visual reference): one row for each action, its
 * key in a key cap at the right (`key-cap.tsx`). A key another action holds is a clash: both caps are in the warning
 * colour and the changed row's note says the two swap on Apply. A row whose pending key is not its default offers
 * Reset. Move is one row, as the design draws it, with a cap for each direction, W, A, S and D, each chosen, waiting,
 * clashing and reset like any other cap and labelled by the character the player's layout prints on its physical key
 * (ADR-061's amendment "Move joins the key table"); the arrows, Move's fixed second set, are said in the group's name,
 * since the design has no place to show them. Open chat and Jump are
 * shown with the keys they always have. Run is shown with its key, Shift, which is fixed, and with its setting, Hold
 * or Toggle (ADR-116 decisions 8 and 11), a row of the Apply model like every other.
 *
 * A note stands under its own row, as Render resolution's does, where the design draws one note under a list of
 * seven: this list is longer than the pane, and a note at its end would be out of sight of the row it speaks of.
 *
 * For assistive technology a chosen key is said once ("Interact: F") in a region of its own, a refusal is said again
 * each time it happens, and a changed row's cap says so in its own name.
 */

export { KEY_CAP };

/**
 * The last key refused: the action it was for, the key, why, and which refusal it was, so the same one twice is two.
 */
interface Refusal {
  readonly action: ActionId;
  readonly code: string;
  readonly reason: RefusedReason;
  readonly count: number;
  /** For a refused swap, the action it would leave on a key it cannot use, and that key. */
  readonly left?: { readonly action: ActionId; readonly code: string };
}

/**
 * What the pane reports.
 */
export interface ControlsEvents {
  /**
   * A key was chosen for an action.
   *
   * @param action The action.
   * @param code The physical key.
   */
  readonly onKey: (action: ActionId, code: string) => void;
  /**
   * An action's Reset was pressed.
   *
   * @param action The action.
   */
  readonly onResetKey: (action: ActionId) => void;
  /**
   * Run's Hold or Toggle was chosen.
   *
   * @param mode The mode.
   */
  readonly onRunMode: (mode: RunMode) => void;
  /**
   * A Keyboard layout was chosen (P1.9-T174).
   *
   * @param choice The choice.
   */
  readonly onLayout: (choice: LayoutChoice) => void;
  /**
   * Takes Lock the ability bar as a pending change (ADR-121 decision 3.2).
   *
   * @param locked Whether the bar is locked.
   */
  readonly onBarLocked?: ((locked: boolean) => void) | undefined;
  /**
   * Asks to reset the ability bar (ADR-121 decision 2.5), which Settings confirms; absent where the bar cannot be reset
   * now, and the row's button is then disabled.
   */
  readonly onBarReset?: (() => void) | undefined;
}

/**
 * The Keyboard layout choices as the row offers them.
 *
 * @returns The choices.
 */
function layoutOptions(): readonly { readonly value: LayoutChoice; readonly label: string }[] {
  return LAYOUT_CHOICES.map((value) => ({ value, label: say(`controls.layoutChoices.${value}`) }));
}

/**
 * What the pane knows of the keys' clashes: the draft's clashes.
 */
interface Conflicts {
  readonly clashes: readonly Clash[];
}

/**
 * Whether an action's cap is in a clash, on either side.
 *
 * @param action The action.
 * @param conflicts The clashes.
 * @returns True when its cap is in the warning colour.
 */
function inConflict(action: ActionId, conflicts: Conflicts): boolean {
  return conflicts.clashes.some((clash) => clash.action === action || clash.holder === action);
}

/**
 * An action's note: a refused key's line, then, while its cap waits, the line that says so; else the clash it is part
 * of as the changed row; else nothing.
 *
 * @param action The action.
 * @param waiting Whether its cap waits.
 * @param refusal The last refusal, or null.
 * @param conflicts The clashes.
 * @param layout The layout keys are named by.
 * @returns The note's lines and whether it warns.
 */
function actionNote(action: ActionId, waiting: boolean, refusal: Refusal | null, conflicts: Conflicts, layout: KeyLayout): { readonly lines: readonly NoteLine[]; readonly warning: boolean } {
  const refused = refusal !== null && refusal.action === action ? [{ id: `refused-${refusal.count}`, text: refusal.left === undefined ? refusedKeyNote(codeLabel(refusal.code, layout), refusal.reason) : say("controls.swapRefused", { action: actionName(refusal.left.action), key: codeLabel(refusal.left.code, layout) }) }] : [];
  if (waiting) {
    return { lines: [...refused, { id: "waiting", text: say("controls.waiting", { action: actionName(action) }) }], warning: false };
  }
  if (refused.length > 0) {
    return { lines: refused, warning: false };
  }
  const clash = conflicts.clashes.find((made) => made.action === action);
  return clash === undefined ? { lines: [], warning: false } : { lines: [{ id: "clash", text: say("controls.clash", { key: codeLabel(clash.code, layout), holder: actionName(clash.holder) }) }], warning: true };
}

/**
 * What a cap needs from the pane: its draft and state, and what it reports.
 */
interface CapProps {
  readonly draft: SettingsDraft;
  readonly waiting: ActionId | null;
  readonly refusal: Refusal | null;
  readonly conflicts: Conflicts;
  readonly layout: KeyLayout;
  readonly onWait: (action: ActionId) => void;
  readonly onRefuse: (action: ActionId, code: string, reason: RefusedReason) => void;
  readonly onChoose: (action: ActionId, code: string) => void;
  readonly onDone: (action: ActionId) => void;
}

/**
 * An action's cap in its row, wired to the pane.
 *
 * @param action The action.
 * @param props The pane's part of the cap.
 * @param describedBy The id of the row's note, while it holds a line.
 * @returns The cap.
 */
function actionCap(action: ActionId, props: CapProps, describedBy: string | undefined): ReactElement {
  const waits = props.waiting === action;
  const name = actionName(action);
  const label = codeLabel(props.draft.pending.keys[action], props.layout);
  const state: CapState = waits ? "wait" : inConflict(action, props.conflicts) ? "clash" : "rest";
  return <KeyCap action={action} label={label} state={state} name={waits ? say("controls.capWaiting", { action: name }) : capName(name, label, isChanged(props.draft, keySetting(action)))} describedBy={describedBy} onWait={() => props.onWait(action)} onRefuse={(code, reason) => props.onRefuse(action, code, reason)} onChoose={(code) => props.onChoose(action, code)} onDone={() => props.onDone(action)} />;
}

/**
 * A row of key caps: its name, Reset while a pending key in it is not its default, and its caps; under it, its note.
 *
 * @param props The row's setting `id` and `name`; the `actions` its caps hold, in order; the `note`; whether it is
 *   `changed`; the group's accessible `group` name; `onReset`, while it offers Reset; and the pane's part of each cap.
 * @returns The row and its note.
 */
function CapsRow({ id, name, actions, note, changed, group, onReset, caps }: { readonly id: SettingId; readonly name: string; readonly actions: readonly ActionId[]; readonly note: { readonly lines: readonly NoteLine[]; readonly warning: boolean }; readonly changed: boolean; readonly group: string; readonly onReset: (() => void) | null; readonly caps: CapProps }): ReactElement {
  const height = rowHeight(KEY_CAP.height);
  const describedBy = note.lines.length > 0 ? noteId(id) : undefined;
  return (
    <>
      <SettingsRow id={id} label={name} changed={changed} control={height}>
        <div className="ot-game-settings__keys" role="group" aria-label={group} style={{ gap: logical(KEY_CAP.resetGap) }}>
          {onReset === null ? null : (
            <span className="ot-game-settings__part" data-control-reset={actions.length === 1 ? actions[0] : name} style={{ marginTop: logical(centreOffset(height, pixelType("label").lineHeight)) }}>
              <TextLink onClick={onReset}>{say("controls.resetOne")}</TextLink>
            </span>
          )}
          <span className="ot-game-settings__part ot-game-settings__keys" style={{ marginTop: logical(centreOffset(height, KEY_CAP.height)), gap: logical(KEY_CAP.capGap) }}>
            {actions.map((action) => (
              <Fragment key={action}>{actionCap(action, caps, describedBy)}</Fragment>
            ))}
          </span>
        </div>
      </SettingsRow>
      <KeyNote id={id} lines={note.lines} warning={note.warning} />
    </>
  );
}

/**
 * A row for an action whose keys are fixed: its name and a cap that is not a button.
 *
 * @param props The action's `name` and its keys' `label`.
 * @returns The row.
 */
function FixedRow({ name, label }: { readonly name: string; readonly label: string }): ReactElement {
  const height = rowHeight(KEY_CAP.height);
  return (
    <div className="ot-game-settings__row" data-control-fixed={name} style={{ minHeight: logical(height), gap: logical(SETTINGS_WINDOW.controlGap) }}>
      <div className="ot-game-settings__name" style={{ marginTop: logical(centreOffset(height, BODY_TYPE.body.lineHeight)) }}>
        <span className="ot-game-settings__label">{name}</span>
      </div>
      <div className="ot-game-settings__control" style={{ marginTop: logical(centreOffset(height, KEY_CAP.height)) }}>
        <span className="ot-game-settings__cap is-fixed" role="img" aria-label={say("controls.fixed", { action: name, keys: label })} style={{ minWidth: logical(KEY_CAP.minWidth), height: logical(KEY_CAP.height), padding: `0 ${logical(KEY_CAP.paddingX)}` }}>
          {label}
        </span>
      </div>
    </div>
  );
}

/**
 * Sprint's row (ADR-116 decisions 8 and 11; ADR-061's amendment for P1.9-T174, point 3): its Hold or Toggle choice and,
 * after it, its key's cap, chosen, waiting, clashing and reset like any other; under it, its note.
 *
 * @param props The `draft`; `onMode`, told the mode chosen; the key's `note`; `onReset`, while its key is off its
 *   default; and the pane's part of the cap.
 * @returns The row and its note.
 */
function RunRow({ draft, onMode, note, onReset, caps }: { readonly draft: SettingsDraft; readonly onMode: (mode: RunMode) => void; readonly note: { readonly lines: readonly NoteLine[]; readonly warning: boolean }; readonly onReset: (() => void) | null; readonly caps: CapProps }): ReactElement {
  const id = keySetting("run");
  const changed = isChanged(draft, "runMode") || isChanged(draft, id);
  const height = KIT_METRICS.button.height;
  const name = actionName("run");
  const describedBy = note.lines.length > 0 ? noteId(id) : undefined;
  return (
    <>
      <SettingsRow id="runMode" label={name} changed={changed} control={height}>
        <div className="ot-game-settings__keys" role="group" aria-label={name} style={{ gap: logical(KEY_CAP.resetGap) }}>
          {onReset === null ? null : (
            <span className="ot-game-settings__part" data-control-reset="run" style={{ marginTop: logical(centreOffset(height, pixelType("label").lineHeight)) }}>
              <TextLink onClick={onReset}>{say("controls.resetOne")}</TextLink>
            </span>
          )}
          <Segmented label={say("controls.runMode")} describedBy={isChanged(draft, "runMode") ? changedMarkId("runMode") : undefined} options={RUN_MODE_CHOICES} value={draft.pending.runMode} onChange={onMode} />
          <span className="ot-game-settings__part" style={{ marginTop: logical(centreOffset(height, KEY_CAP.height)) }}>
            {actionCap("run", caps, describedBy)}
          </span>
        </div>
      </SettingsRow>
      <KeyNote id={id} lines={note.lines} warning={note.warning} />
    </>
  );
}

/**
 * The Keyboard layout row (ADR-061's amendment for P1.9-T174, point 8): Auto or a preset, chosen from the kit's
 * dropdown (ADR-061's note for P1.9-T175), a pending change Apply keeps, which changes what every cap says and nothing a
 * key does.
 *
 * @param props The `draft` and `onLayout`, told the choice made.
 * @returns The row.
 */
function LayoutRow({ draft, onLayout }: { readonly draft: SettingsDraft; readonly onLayout: (choice: LayoutChoice) => void }): ReactElement {
  const changed = isChanged(draft, "keyLayout");
  return (
    <SettingsRow id="keyLayout" label={say("controls.layout")} changed={changed} control={KIT_METRICS.button.height}>
      <Dropdown label={say("controls.layout")} describedBy={changed ? changedMarkId("keyLayout") : undefined} options={layoutOptions()} value={draft.pending.keyLayout} onChange={onLayout} />
    </SettingsRow>
  );
}

/**
 * Lock the ability bar's row (ADR-121 decision 3.2): a checkbox, a pending change Apply keeps.
 *
 * @param props The `draft` and `onLocked`, told the box's new state.
 * @returns The row.
 */
function BarLockRow({ draft, onLocked }: { readonly draft: SettingsDraft; readonly onLocked: (locked: boolean) => void }): ReactElement {
  const changed = isChanged(draft, "barLocked");
  return (
    <SettingsRow id="barLocked" label={say("controls.barLock")} changed={changed} control={KIT_METRICS.check.row}>
      <Checkbox label={say("controls.barLock")} labelHidden describedBy={changed ? changedMarkId("barLocked") : undefined} checked={draft.pending.barLocked} onChange={onLocked} />
    </SettingsRow>
  );
}

/**
 * Reset ability bar's row (ADR-121 decision 2.5): a button that asks Settings to confirm the reset, disabled while the
 * bar cannot be reset (never arranged, not yet arrived, or frozen).
 *
 * @param props `onReset`, absent while the bar cannot be reset.
 * @returns The row.
 */
function BarResetRow({ onReset }: { readonly onReset: (() => void) | undefined }): ReactElement {
  const height = KIT_METRICS.button.height;
  return (
    <div className="ot-game-settings__row" data-control-reset-bar="" style={{ minHeight: logical(height), gap: logical(SETTINGS_WINDOW.controlGap) }}>
      <div className="ot-game-settings__name" style={{ marginTop: logical(centreOffset(height, BODY_TYPE.body.lineHeight)) }}>
        <span className="ot-game-settings__label">{say("controls.barReset")}</span>
      </div>
      <div className="ot-game-settings__control">
        <Button label={say("controls.barReset")} disabled={onReset === undefined} {...(onReset === undefined ? {} : { onClick: onReset })}>
          {say("controls.barResetButton")}
        </Button>
      </div>
    </div>
  );
}

/**
 * Controls' rows.
 *
 * @param props The settings the pane shows (`rows`), the `draft` they are drawn from and what the pane reports.
 * @returns The rows, in the table's order: Move's caps, the fixed actions, Run, and the ability bar's lock and reset
 *   among them.
 */
export function ControlsPane({ rows, draft, events }: { readonly rows: readonly SettingId[]; readonly draft: SettingsDraft; readonly events: ControlsEvents }): ReactElement {
  const [waiting, setWaiting] = useState<ActionId | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [said, setSaid] = useState<{ readonly text: string; readonly count: number }>({ text: "", count: 0 });
  const conflicts: Conflicts = { clashes: clashesOf(draft.applied.keys, draft.pending.keys) };
  const layout = pageLayout.layoutFor(draft.pending.keyLayout);

  /**
   * Records a refused key for an action, as a new refusal each time.
   *
   * @param action The action.
   * @param code The key.
   * @param reason Why.
   * @param left For a refused swap, the action it would leave on a key it cannot use, and that key.
   */
  const refuse = (action: ActionId, code: string, reason: RefusedReason, left?: { readonly action: ActionId; readonly code: string }): void => setRefusal((last) => ({ action, code, reason, count: (last?.count ?? 0) + 1, ...(left === undefined ? {} : { left }) }));

  /**
   * Takes a key for an action unless what Apply would make of it is refused, and says what was taken.
   *
   * @param action The action.
   * @param code The key.
   * @returns True when the key was taken.
   */
  const take = (action: ActionId, code: string): boolean => {
    const left = refusalOf(draft.applied.keys, draft.pending.keys, action, code);
    if (left !== null) {
      refuse(action, code, "focus", left);
      return false;
    }
    setRefusal(null);
    setSaid((last) => ({ text: say("controls.said", { action: actionName(action), key: codeLabel(code, layout) }), count: last.count + 1 }));
    return true;
  };

  /**
   * A row's Reset: each of its actions off its default goes back to it, one after another as Apply would weigh them,
   * unless one would leave an action on a key it cannot use; then nothing is reset and that one is refused.
   *
   * @param name The row's name, said with the keys it went back to.
   * @param actions The row's actions off their defaults.
   */
  const reset = (name: string, actions: readonly ActionId[]): void => {
    let pending = draft.pending.keys;
    for (const action of actions) {
      const left = refusalOf(draft.applied.keys, pending, action, DEFAULT_BINDINGS[action]);
      if (left !== null) {
        refuse(action, DEFAULT_BINDINGS[action], "focus", left);
        return;
      }
      pending = chooseKey(draft.applied.keys, pending, action, DEFAULT_BINDINGS[action]);
    }
    setRefusal(null);
    setSaid((last) => ({ text: say("controls.said", { action: name, key: actions.map((action) => codeLabel(DEFAULT_BINDINGS[action], layout)).join(" ") }), count: last.count + 1 }));
    for (const action of actions) {
      events.onResetKey(action);
    }
  };

  const caps: CapProps = {
    draft,
    waiting,
    refusal,
    conflicts,
    layout,
    onWait: (action) => {
      setRefusal(null);
      setWaiting(action);
    },
    onRefuse: (action, code, reason) => refuse(action, code, reason),
    onChoose: (action, code) => {
      if (take(action, code)) {
        events.onKey(action, code);
        setWaiting((current) => (current === action ? null : current));
      }
    },
    onDone: (action) => {
      setRefusal(null);
      setWaiting((current) => (current === action ? null : current));
    },
  };

  /**
   * The note of a row of actions: that of the action whose cap waits, else the one last refused, else the one whose
   * pending key clashes.
   *
   * @param actions The row's actions.
   * @returns The note.
   */
  const noteOf = (actions: readonly ActionId[]): { readonly lines: readonly NoteLine[]; readonly warning: boolean } => {
    const noted = actions.find((action) => waiting === action) ?? actions.find((action) => refusal?.action === action) ?? actions.find((action) => conflicts.clashes.some((clash) => clash.action === action));
    return noted === undefined ? { lines: [], warning: false } : actionNote(noted, waiting === noted, refusal, conflicts, layout);
  };

  /**
   * A row of caps for some actions, or null when the pane shows none of them.
   *
   * @param id The row's setting.
   * @param name The row's name.
   * @param group The accessible name of its caps' group.
   * @param all The row's actions.
   * @returns The row, or null.
   */
  const capsRow = (id: SettingId, name: string, group: string, all: readonly ActionId[]): ReactElement | null => {
    const actions = all.filter((action) => rows.includes(keySetting(action)));
    if (actions.length === 0) {
      return null;
    }
    const off = actions.filter((action) => draft.pending.keys[action] !== DEFAULT_BINDINGS[action]);
    return <CapsRow key={id} id={id} name={name} actions={actions} note={noteOf(actions)} changed={actions.some((action) => isChanged(draft, keySetting(action)))} group={group} onReset={off.length === 0 ? null : () => reset(name, off)} caps={caps} />;
  };

  return (
    <>
      {rows.includes("keyLayout") ? <LayoutRow key="keyLayout" draft={draft} onLayout={events.onLayout} /> : null}
      {CONTROL_ROWS.map((row) => {
        if (row.kind === "move") {
          const name = row.set === "keys" ? say("controls.move") : say("controls.moveArrows");
          return capsRow(keySetting(MOVE_SETS[row.set][0] as ActionId), name, name, MOVE_SETS[row.set]);
        }
        if (row.kind === "fixed") {
          return <FixedRow key={row.nameKey} name={say(row.nameKey)} label={row.label} />;
        }
        if (row.kind === "run") {
          const off = draft.pending.keys.run !== DEFAULT_BINDINGS.run;
          return rows.includes("runMode") ? <RunRow key="run" draft={draft} onMode={events.onRunMode} note={noteOf(["run"])} onReset={off ? () => reset(actionName("run"), ["run"]) : null} caps={caps} /> : null;
        }
        if (row.kind === "barLock") {
          const onLocked = events.onBarLocked;
          return rows.includes("barLocked") && onLocked !== undefined ? <BarLockRow key="barLock" draft={draft} onLocked={onLocked} /> : null;
        }
        if (row.kind === "barReset") {
          return rows.includes("barLocked") && events.onBarLocked !== undefined ? <BarResetRow key="barReset" onReset={events.onBarReset} /> : null;
        }
        return capsRow(keySetting(row.id), actionName(row.id), actionName(row.id), [row.id]);
      })}
      <div className="ot-game-settings__said" aria-live="polite" data-game-menu="controls-said">
        {said.text === "" ? null : <span key={said.count}>{said.text}</span>}
      </div>
    </>
  );
}
